import React, { useState } from 'react';

import { Box, ButtonGroup, Card, Flex, Skeleton, Text } from '@chakra-ui/react';
import { DeleteIcon, IconButton } from '@firehydrant/design-system';
import { useUserContext } from 'contexts/UserContext/UserContextProvider';
import { queryClient } from 'helpers/SPAWrapper';
import { useOrgNavigate } from 'hooks/useOrgNavigate';
import PropTypes from 'prop-types';
import { usePhones } from 'queries/notificationSettings/phones';
import { usePushNotificationDevices } from 'queries/notificationSettings/pushNotificationDevices';
import { useOnboardingStep } from 'queries/onboardingTasks';
import {
  useCreateSignalsNotification,
  useDeleteSignalsNotification,
  useSignalNotifications,
  useUpdateSignalsNotification,
} from 'queries/signalsNotifications';
import { FiCheckCircle, FiCopy, FiPlus, FiX } from 'react-icons/fi';
// queries
import * as Yup from 'yup';

// components
import FormV2 from 'components/common/Form/FormV2/FormV2';
import { useToastContext } from 'components/common/ToastBannerProvider/ToastBannerProvider';

import { NotificationIcon } from './NotificationIcon';
import {
  generateInitialValues,
  generateNotificationOptions,
  timeOptions,
} from './helpers/signalsNotificationFormHelpers';

const validationSchema = Yup.object().shape({
  notifications: Yup.array().of(
    Yup.object().shape({
      notification_type: Yup.object().required('Required'),
      delay: Yup.object().required('Required'),
    }),
  ),
});

const SignalsNotificationsForm = () => {
  const { addToast } = useToastContext();
  const { data: signalsNotifications, isLoading } = useSignalNotifications();
  const { data: phones } = usePhones();
  const { data: pushNotificationDevices } = usePushNotificationDevices();
  const { isSignalsOnboarding, completeOnboardingStep, isStepComplete } =
    useOnboardingStep('configure_notification_preferences');
  const { currentUser } = useUserContext();
  const navigate = useOrgNavigate();
  const notificationOptions = generateNotificationOptions(
    currentUser,
    phones,
    pushNotificationDevices,
  );

  const [editingIndex, setEditingIndex] = useState(null);

  const toggleEditState = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else {
      setEditingIndex(index);
    }
  };

  const onSuccess = () => {
    if (isSignalsOnboarding && !isStepComplete) {
      completeOnboardingStep();
    }
    if (isSignalsOnboarding) {
      navigate('/overview');
    }
    addToast({
      message: 'Notification setting has been updated.',
      variant: 'success',
    });
    setEditingIndex(null);
    queryClient.invalidateQueries('signals_notification_settings');
  };

  const onDeleteSuccess = () => {
    addToast({
      message: 'Notification setting has been deleted.',
      variant: 'success',
    });
    setEditingIndex(null);
    queryClient.invalidateQueries('signals_notification_settings');
  };

  const onError = () => {
    addToast({
      message: 'Something went wrong updating notification settings.',
      variant: 'alert',
    });
  };

  const { mutate: createNotification } = useCreateSignalsNotification({
    onSuccess,
    onError,
  });
  const { mutate: updateNotification } = useUpdateSignalsNotification({
    onSuccess,
    onError,
  });
  const { mutate: deleteNotification } = useDeleteSignalsNotification({
    onSuccess: onDeleteSuccess,
    onError,
  });

  const onSubmit = (values) => {
    const submitValues = values.notifications.map((item) => {
      return {
        notification_type: item.notification_type.notification_type,
        destination: item.notification_type.value,
        delay: item.delay.value,
        id: item.id,
      };
    })[editingIndex];

    if (submitValues.id) {
      return updateNotification(submitValues);
    } else {
      return createNotification(submitValues);
    }
  };

  const deletePreference = (item, index, removeFunction) => {
    if (item.id) {
      deleteNotification(item);
    } else {
      removeFunction(index);
    }
  };

  const duplicatePreference = (item) => {
    const copy = {
      notification_type: item.notification_type.notification_type,
      destination: item.notification_type.value,
      // add 5 minutes to the iso8601 duration
      delay: item.delay.value,
    };
    createNotification(copy);
  };

  const cancelEdit = (resetForm) => {
    resetForm();
    toggleEditState(null);
  };

  const initialValues = generateInitialValues(signalsNotifications);

  return (
    <>
      <FormV2
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        maxWidth="100%"
        requiredPermissions={['member', 'owner']}
      >
        {(formik) => {
          return (
            <>
              <Flex width="100%" gap="2"></Flex>
              <Text as="h3" mb="2" mt="">
                Alert Notification Preferences
              </Text>
              <Box backgroundColor="blackAlpha.50" p="2">
                <FormV2.Group>
                  <FormV2.FieldArray name="notifications">
                    {(arrayHelpers) => {
                      return (
                        <>
                          {arrayHelpers.form.values.notifications.map(
                            (item, index) => (
                              <Card key={index} py="3" px="2" my="2">
                                <Flex alignItems="center">
                                  {editingIndex === index ? (
                                    <NotificationEditRow
                                      index={index}
                                      notificationOptions={notificationOptions}
                                      item={item}
                                      formik={formik}
                                      cancelEdit={cancelEdit}
                                      duplicatePreference={duplicatePreference}
                                      deletePreference={deletePreference}
                                      arrayHelpers={arrayHelpers}
                                      // editDestination
                                    />
                                  ) : (
                                    <NotificationRow
                                      index={index}
                                      item={item}
                                      formik={formik}
                                      toggleEditState={toggleEditState}
                                      isLoading={isLoading}
                                    />
                                  )}
                                </Flex>
                              </Card>
                            ),
                          )}
                          <FormV2.ArrayAddButton
                            buttonText="Add notification step"
                            buttonProps={{
                              width: '100%',
                              borderStyle: 'dashed',
                            }}
                            addAction={() => {
                              const lastElement =
                                arrayHelpers.form.values.notifications[
                                  arrayHelpers.form.values.notifications
                                    .length - 1
                                ];

                              if (
                                lastElement &&
                                (lastElement.notification_type === '' ||
                                  lastElement.delay === '')
                              ) {
                                addToast({
                                  message:
                                    'Please complete the previous notification before adding another.',
                                  variant: 'info',
                                });
                              } else {
                                if (editingIndex !== null) {
                                  formik.handleSubmit();
                                } else {
                                  arrayHelpers.push({
                                    notification_type: '',
                                    delay: '',
                                  });
                                  setEditingIndex(
                                    arrayHelpers.form.values.notifications
                                      .length,
                                  );
                                }
                              }
                            }}
                          />
                        </>
                      );
                    }}
                  </FormV2.FieldArray>
                </FormV2.Group>
              </Box>
            </>
          );
        }}
      </FormV2>
    </>
  );
};

const NotificationMessage = ({ notification }) => {
  let text;

  switch (notification.notification_type.notification_type) {
    case 'email':
      text = `Send me an email at ${notification.notification_type.label}`;
      break;
    case 'slack':
      text = `Send me a Slack message`;
      break;
    case 'sms':
      text = `Send me a text message at ${notification.notification_type.label}`;
      break;
    case 'whats_app':
      text = `Send me a text message at ${notification.notification_type.label}`;
      break;
    case 'voice':
      text = `Call me at ${notification.notification_type.label}`;
      break;
    case 'apns':
      text = `Send me a push notification`;
      break;
    case 'fcm':
      text = `Send me a push notification`;
      break;
    default:
      text = null;
  }
  return <Text mb="0">{text}</Text>;
};

const NotificationLabel = ({ notification }) => {
  let text;

  switch (notification.notification_type.notification_type) {
    case 'email':
      text = `Email`;
      break;
    case 'slack':
      text = `Slack`;
      break;
    case 'sms':
      text = `SMS`;
      break;
    case 'voice':
      text = `Voice`;
      break;
    case 'whats_app':
      text = `WhatsApp`;
      break;
    case 'apns':
      text = `Push Notifications`;
      break;
    case 'fcm':
      text = `Push Notifications`;
      break;
    default:
      text = null;
  }

  return (
    <Text mb="0" display="inline-block" pl="1">
      {text}
    </Text>
  );
};

NotificationMessage.propTypes = {
  notification: PropTypes.object.isRequired,
};

const NotificationEditRow = ({
  index,
  notificationOptions,
  item,
  formik,
  cancelEdit,
  duplicatePreference,
  deletePreference,
  arrayHelpers,
  editDestination = false,
}) => {
  return (
    <FormV2.ArrayTwoColumn>
      {editDestination || !item.id ? (
        <>
          <Flex minWidth="9%" alignItems="center"></Flex>
          <FormV2.Dropdown
            name={`notifications[${index}].notification_type`}
            arrayItemIndex={index}
            options={notificationOptions}
            placeholder="Select Notification Type"
            data-testid="notification-type"
          />
        </>
      ) : (
        <>
          <Flex minWidth="19.2%" pl="4" alignItems="center">
            <NotificationIcon
              type={item.notification_type?.notification_type}
            />
            <NotificationLabel notification={item} />
          </Flex>
          <Box minWidth="35%">
            <NotificationMessage notification={item} />
          </Box>
        </>
      )}

      <Flex width="100%" justifyContent="space-between" gap="4">
        <FormV2.Dropdown
          name={`notifications[${index}].delay`}
          arrayItemIndex={index}
          options={timeOptions}
          placeholder="Select Delay"
          data-testid="delay"
        />
        <Flex mt="1">
          <Box display="none">
            <FormV2.Input name={`notifications[${index}].id`} />
          </Box>
          <ButtonGroup>
            <IconButton
              type="submit"
              icon={item.id ? <FiCheckCircle /> : <FiPlus />}
              variant="outline"
              title={item.id ? 'Update' : 'Create'}
            />
            {item.id && (
              <>
                <IconButton
                  onClick={() => duplicatePreference(item)}
                  variant="outline"
                  icon={<FiCopy />}
                  title="Duplicate"
                />
                <IconButton
                  variant="outline"
                  icon={<DeleteIcon />}
                  data-testid="delete-button"
                  onClick={() =>
                    deletePreference(item, index, arrayHelpers.remove)
                  }
                  title="delete"
                />
              </>
            )}
            <IconButton
              onClick={() => cancelEdit(formik.resetForm)}
              variant="ghost"
              icon={<FiX />}
              title="Clear"
            />
          </ButtonGroup>
        </Flex>
      </Flex>
    </FormV2.ArrayTwoColumn>
  );
};

NotificationEditRow.propTypes = {
  index: PropTypes.number.isRequired,
  notificationOptions: PropTypes.array.isRequired,
  item: PropTypes.object.isRequired,
  formik: PropTypes.object.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  duplicatePreference: PropTypes.func.isRequired,
  deletePreference: PropTypes.func.isRequired,
  arrayHelpers: PropTypes.object.isRequired,
  editDestination: PropTypes.bool,
};

const NotificationRow = ({
  index,
  item,
  toggleEditState,
  isLoading,
  formik,
}) => {
  if (isLoading || !item.notification_type?.notification_type) {
    return <Skeleton height="20px" width="100%" />;
  }
  return (
    <Flex
      width="100%"
      alignItems="center"
      onClick={() => {
        formik.resetForm();
        toggleEditState(index);
      }}
      cursor="pointer"
      data-testid="edit-button"
    >
      <Flex minWidth="20%" pl="4" alignItems="center">
        <NotificationIcon type={item.notification_type?.notification_type} />
        <NotificationLabel notification={item} />
      </Flex>
      <Box minWidth="35%">
        <NotificationMessage notification={item} />
      </Box>
      <Box minWidth="25%">
        <Text display="inline-block" mb="0">
          {item.delay?.label}
        </Text>
      </Box>
    </Flex>
  );
};

NotificationRow.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  toggleEditState: PropTypes.func.isRequired,
};

export default SignalsNotificationsForm;
